import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/utils";
import { useCartStore } from "@/store/cartStore";
import type { ShopifyProduct } from "@/lib/shopify";
import { getColorHexSync } from "@/lib/colorService";
import { getJewelryGradient } from "@/lib/colorGradient";
import { EngravingOptions, getEngravingCount, ENGRAVING_PRICE, validateEngraving } from "@/components/EngravingOptions";
import { IconSelection, hasIconTag, ICON_PRICE } from "@/components/IconSelection";
import type { EngravingData } from "@/components/EngravingOptions";
import type { IconData } from "@/components/IconSelection";
import { alertToast } from "@/contexts/AlertToastContext";

interface VariantQuickPickerProps {
  product: ShopifyProduct;
  open: boolean;
  onClose: () => void;
}

export const VariantQuickPicker = ({ product, open, onClose }: VariantQuickPickerProps) => {
  const addItem = useCartStore((s) => s.addItem);
  
  const options = product.options || [];
  const variants = product.variants?.edges || [];
  
  // Initialize selected options with first available values
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const opt of options) {
      if (opt.name !== "Title" && opt.values.length > 0) {
        initial[opt.name] = opt.values[0];
      }
    }
    return initial;
  });

  // Engraving & Icons
  const engravingCount = getEngravingCount(product.tags);
  const requiresEngraving = engravingCount > 0;
  const showIcons = hasIconTag(product.tags);
  const [engravingData, setEngravingData] = useState<EngravingData | null>(null);
  const [engravingErrors, setEngravingErrors] = useState<{ font?: string; texts?: string[] }>({});
  const [iconData, setIconData] = useState<IconData | null>(null);
  const [noEngraving, setNoEngraving] = useState(false);

  // Check if product has "מילים" (words) option
  const wordsOption = options.find(opt => 
    opt.name === 'מילים' || opt.name.toLowerCase() === 'words'
  );
  const hasWordsOption = !!wordsOption;

  // Find matching variant
  const selectedVariant = useMemo(() => {
    return variants.find((v) => {
      return v.node.selectedOptions.every(
        (so) => selectedOptions[so.name] === so.value || so.name === "Title"
      );
    })?.node;
  }, [selectedOptions, variants]);

  // Count words in all engraving texts
  const getTotalWordCount = (): number => {
    if (!engravingData?.texts) return 0;
    return engravingData.texts.reduce((total, text) => {
      const words = text.trim().split(/\s+/).filter(Boolean);
      return total + words.length;
    }, 0);
  };

  // Auto-select "מילים" variant based on word count or noEngraving checkbox
  useEffect(() => {
    if (!hasWordsOption || !product) return;
    
    let targetValue: string;
    if (noEngraving) {
      targetValue = '0';
    } else {
      const wordCount = getTotalWordCount();
      targetValue = Math.min(wordCount, 3).toString();
    }
    
    const wordsOptionName = wordsOption?.name || 'מילים';
    if (selectedOptions[wordsOptionName] !== targetValue) {
      const newOptions = { ...selectedOptions, [wordsOptionName]: targetValue };
      setSelectedOptions(newOptions);
    }
  }, [engravingData, noEngraving, hasWordsOption, product, wordsOption?.name]);

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions((prev) => ({ ...prev, [optionName]: value }));
  };

  const handleAdd = () => {
    if (!selectedVariant) return;

    // Validate engraving if required and not disabled
    if (requiresEngraving && !noEngraving) {
      const errors = validateEngraving(engravingData, engravingCount);
      if (Object.keys(errors).length > 0) {
        setEngravingErrors(errors);
        alertToast.error("יש למלא את פרטי החריטה", "בחר פונט והזן טקסט לחריטה");
        return;
      }
      setEngravingErrors({});
    }

    addItem({
      product,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: 1,
      selectedOptions: selectedVariant.selectedOptions,
      variantImage: selectedVariant.image || undefined,
      engraving: requiresEngraving && engravingData && !noEngraving
        ? { font: engravingData.font, texts: engravingData.texts, price: ENGRAVING_PRICE }
        : undefined,
      icons: showIcons && iconData && iconData.iconIds.length > 0 ? iconData.iconIds : undefined,
    });
    onClose();
  };

  // Resolve the display image: prefer selected variant's image, then fallback to product image
  const displayImage = useMemo(() => {
    if (selectedVariant?.image?.url) return selectedVariant.image.url;
    return product.images?.edges?.[0]?.node?.url;
  }, [selectedVariant, product]);

  const isColorOption = (name: string) => {
    const lower = name.toLowerCase();
    return lower.includes("צבע") || lower.includes("color") || lower.includes("colour") || lower.includes("גוון") || lower.includes("metal") || lower.includes("מתכת");
  };

  const isWordsOption = (name: string) => {
    const lower = name.toLowerCase();
    return lower === 'מילים' || lower === 'words';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto p-5 bg-white" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-base">{product.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product image - white background, updates with variant selection */}
          {displayImage && (
            <div className="w-full h-48 bg-white rounded-lg overflow-hidden relative">
              <img 
                key={displayImage} 
                src={displayImage} 
                alt={product.title} 
                className="w-full h-full object-contain animate-fade-in" 
              />
            </div>
          )}

          {/* Options - hide "מילים" since it's auto-selected */}
          {options
            .filter((o) => o.name !== "Title" && o.values.length > 1 && !isWordsOption(o.name))
            .map((option) => (
              <div key={option.name} className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {option.name}
                </label>

                {isColorOption(option.name) ? (
                  // Color swatches
                  <div className="flex flex-wrap gap-2">
                    {option.values.map((value) => {
                      const hex = getColorHexSync(value);
                      const isSelected = selectedOptions[option.name] === value;
                      const isWhite = hex.toLowerCase() === "#ffffff" || hex.toLowerCase() === "#fffff0" || hex.toLowerCase() === "#f5f5f0" || hex.toLowerCase() === "#e8e8e8" || hex.toLowerCase() === "#e5e4e2";

                      return (
                        <button
                          key={value}
                          onClick={() => handleOptionChange(option.name, value)}
                          className={cn(
                            "w-8 h-8 rounded-full border-2 transition-all relative",
                            isSelected
                              ? "border-primary ring-2 ring-primary/30 scale-110"
                              : "border-border hover:border-primary/50"
                          )}
                          style={{ background: getJewelryGradient(hex) }}
                          title={value}
                        >
                          {isWhite && (
                            <span className="absolute inset-[2px] rounded-full border border-gray-300" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // Regular option buttons
                  <div className="flex flex-wrap gap-1.5">
                    {option.values.map((value) => (
                      <button
                        key={value}
                        onClick={() => handleOptionChange(option.name, value)}
                        className={cn(
                          "px-3 py-1.5 text-xs rounded border transition-all",
                          selectedOptions[option.name] === value
                            ? "border-primary bg-primary/10 text-foreground font-medium"
                            : "border-border text-muted-foreground hover:border-primary/40"
                        )}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {/* Engraving - exactly like Product page */}
          {requiresEngraving && (
            <>
              {/* No Engraving Checkbox - Only show if product has "מילים" option */}
              {hasWordsOption && (
                <div className="flex items-center gap-3 py-2">
                  <Checkbox
                    id="no-engraving-quick"
                    checked={noEngraving}
                    onCheckedChange={(checked) => {
                      setNoEngraving(checked === true);
                      if (checked) {
                        setEngravingData(null);
                        setEngravingErrors({});
                      }
                    }}
                  />
                  <label
                    htmlFor="no-engraving-quick"
                    className="text-sm cursor-pointer select-none"
                    style={{ fontWeight: 300 }}
                  >
                    ללא חריטה
                  </label>
                </div>
              )}

              {!noEngraving && (
                <EngravingOptions
                  value={engravingData}
                  onChange={setEngravingData}
                  price={ENGRAVING_PRICE}
                  currencyCode="ILS"
                  engravingCount={engravingCount}
                  errors={engravingErrors}
                />
              )}
            </>
          )}

          {/* Icons - only when engraving enabled */}
          {showIcons && !noEngraving && (
            <IconSelection
              value={iconData}
              onChange={setIconData}
            />
          )}

          {/* Price + Add button */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              {selectedVariant && (
                <span className="text-lg font-semibold text-primary">
                  {formatPrice(selectedVariant.price.amount, selectedVariant.price.currencyCode)}
                </span>
              )}
              {!selectedVariant && (
                <span className="text-sm text-destructive">וריאנט לא זמין</span>
              )}
            </div>
            <Button
              onClick={handleAdd}
              disabled={!selectedVariant || !selectedVariant.availableForSale}
              className="min-w-[120px]"
            >
              {selectedVariant?.availableForSale === false ? "אזל מהמלאי" : "הוסף לסל"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};